// src/features/dashboard/services/Employee/employeeActiveHours.service.ts

import axios from 'axios';

import api from '../../../../api/apiService';
import { ENDPOINTS } from '../../../../api/endpoints';
import { ENV } from '../../../../config/env';

import {
  ActiveHoursKpiRequest,
  ActiveHoursKpiResponse,

  HourlyActiveMinutesRequest,
  HourlyActiveMinutesResponse,

  TimeUtilisationRequest,
  TimeUtilisationResponse,

  WtdActiveHoursRequest,
  WtdActiveHoursResponse,

  MeVsTeamRequest,
  MeVsTeamResponse,

  FocusBlocksRequest,
  FocusBlocksResponse,

  ActiveHoursHistory30DaysRequest,
  ActiveHoursHistory30DaysResponse,

  WeeklyActiveHoursDetailRequest,
  WeeklyActiveHoursDetailResponse,
} from '../../types/EmployeeActiveHours/employeeActiveHours.types';

import {
  mapActiveHoursKpi,
  mapTimeUtilisation,
  mapWtdActiveHours,
  mapMeVsTeam,
  mapFocusBlocks,
  mapHistory30Days,
  mapWeeklyActiveHoursDetail,
} from '../../mocks/Employee/employeeActiveHours.mock';

/* ============================================================
 * DEFAULT RESPONSES
 * ============================================================ */

const EMPTY_KPI: ActiveHoursKpiResponse = {
  activeToday: '00:00:00',
  utilization: 0,
  sevenDayAvg: '00:00:00',
  peakHour: '',
  vsTeamAvg: '00:00:00',
};

/**
 * IMPORTANT:
 *
 * HourlyActiveMinutesResponse is an OBJECT.
 *
 * The array is inside:
 *
 * employeeHourlyActiveMinutesList
 */
const EMPTY_HOURLY_ACTIVE_MINUTES: HourlyActiveMinutesResponse = {
  hourlyActiveMinutesTarget: 45,

  employeeHourlyActiveMinutesList: [],
};

const EMPTY_TIME_UTILISATION: TimeUtilisationResponse = {
  activePercent: 0,
  activeCount: 0,

  idlePercent: 0,
  idleCount: 0,

  meetingPercent: 0,
  meetingCount: 0,

  totalLoggedHours: '',
  utilization: 0,
};

const EMPTY_ME_VS_TEAM: MeVsTeamResponse = {
  manager: {
    managerCode: '',
    managerName: '',
    employeeName: '',
  },

  trend: [],
};

const EMPTY_FOCUS_BLOCKS: FocusBlocksResponse = {
  blocks: [],

  summary: {
    totalFocus: '',
    avgSwitches: 0,
  },
};

const EMPTY_HISTORY: ActiveHoursHistory30DaysResponse = {
  history: [],
  months: [],
};

/* ============================================================
 * HELPERS
 * ============================================================ */

const toNumber = (
  value: unknown,
  fallback = 0,
): number => {
  const numberValue = Number(value);

  return Number.isFinite(numberValue)
    ? numberValue
    : fallback;
};

/* ============================================================
 * KPI CARDS
 * ============================================================ */

export const getActiveHoursKpiCards = async (
  request: ActiveHoursKpiRequest,
): Promise<ActiveHoursKpiResponse> => {
  try {
    if (ENV.USE_MOCK) {
      return mapActiveHoursKpi(
        EMPTY_KPI,
      );
    }

    const { data } =
      await api.post<ActiveHoursKpiResponse>(
        ENDPOINTS.EMPLOYEE_ACTIVE_HOURS_KPI_CARDS,
        request,
      );

    return mapActiveHoursKpi(data);
  } catch (error) {
    if (axios.isAxiosError(error)) {
      switch (error.response?.status) {
        case 404:
        case 500:
          return mapActiveHoursKpi(
            EMPTY_KPI,
          );
      }
    }

    console.error(
      'Active Hours KPI Error:',
      error,
    );

    return mapActiveHoursKpi(
      EMPTY_KPI,
    );
  }
};

/* ============================================================
 * HOURLY ACTIVE MINUTES
 * ============================================================ */

/**
 * API RESPONSE:
 *
 * {
 *   "hourlyActiveMinutesTarget": "45",
 *   "employeeHourlyActiveMinutesList": [
 *     {
 *       "hour": 0,
 *       "activeMinutes": 0.00
 *     },
 *     {
 *       "hour": 4,
 *       "activeMinutes": 42.08
 *     },
 *     {
 *       "hour": 5,
 *       "activeMinutes": 60.00
 *     }
 *   ]
 * }
 *
 * IMPORTANT:
 *
 * HourlyActiveMinutesResponse = OBJECT
 *
 * employeeHourlyActiveMinutesList = ARRAY
 *
 * DO NOT change this to:
 *
 * HourlyActiveMinutesResponse[]
 */

/* ============================================================
 * NORMALIZE HOURLY RESPONSE
 * ============================================================ */

const normalizeHourlyActiveMinutes = (
  data: HourlyActiveMinutesResponse | null | undefined,
): HourlyActiveMinutesResponse => {
  if (!data) {
    return EMPTY_HOURLY_ACTIVE_MINUTES;
  }

  const target = toNumber(
    data.hourlyActiveMinutesTarget,
    45,
  );

  const list =
    Array.isArray(
      data.employeeHourlyActiveMinutesList,
    )
      ? data.employeeHourlyActiveMinutesList.map(
          (item) => ({
            hour: toNumber(
              item?.hour,
            ),

            activeMinutes:
              toNumber(
                item?.activeMinutes,
              ),
          }),
        )
      : [];

  return {
    hourlyActiveMinutesTarget:
      target > 0
        ? target
        : 45,

    employeeHourlyActiveMinutesList:
      list,
  };
};

/* ============================================================
 * GET HOURLY ACTIVE MINUTES
 * ============================================================ */

export const getHourlyActiveMinutes = async (
  request: HourlyActiveMinutesRequest,
): Promise<HourlyActiveMinutesResponse> => {
  try {
    /* --------------------------------------------------------
     * MOCK
     * -------------------------------------------------------- */

    if (ENV.USE_MOCK) {
      return normalizeHourlyActiveMinutes(
        EMPTY_HOURLY_ACTIVE_MINUTES,
      );
    }

    /* --------------------------------------------------------
     * API
     * -------------------------------------------------------- */

    const { data } =
      await api.post<HourlyActiveMinutesResponse>(
        ENDPOINTS.EMPLOYEE_HOURLY_ACTIVE_MINUTES,
        request,
      );

    /* --------------------------------------------------------
     * DEBUG
     * -------------------------------------------------------- */

    console.log(
      'Hourly Active Minutes API Response:',
      data,
    );

    console.log(
      'Hourly Active Minutes Target:',
      data?.hourlyActiveMinutesTarget,
    );

    console.log(
      'Hourly Active Minutes List:',
      data?.employeeHourlyActiveMinutesList,
    );

    /* --------------------------------------------------------
     * NORMALIZED RESPONSE
     * -------------------------------------------------------- */

    return normalizeHourlyActiveMinutes(
      data,
    );
  } catch (error) {
    if (axios.isAxiosError(error)) {
      switch (error.response?.status) {
        case 404:
        case 500:
          return normalizeHourlyActiveMinutes(
            EMPTY_HOURLY_ACTIVE_MINUTES,
          );
      }
    }

    console.error(
      'Hourly Active Minutes Error:',
      error,
    );

    return normalizeHourlyActiveMinutes(
      EMPTY_HOURLY_ACTIVE_MINUTES,
    );
  }
};

/* ============================================================
 * TIME UTILISATION
 * ============================================================ */

export const getTimeUtilisation = async (
  request: TimeUtilisationRequest,
): Promise<TimeUtilisationResponse> => {
  try {
    if (ENV.USE_MOCK) {
      return mapTimeUtilisation(
        EMPTY_TIME_UTILISATION,
      );
    }

    const { data } =
      await api.post<TimeUtilisationResponse>(
        ENDPOINTS.EMPLOYEE_TIME_UTILISATION,
        request,
      );

    return mapTimeUtilisation(data);
  } catch (error) {
    if (axios.isAxiosError(error)) {
      switch (error.response?.status) {
        case 404:
        case 500:
          return mapTimeUtilisation(
            EMPTY_TIME_UTILISATION,
          );
      }
    }

    console.error(
      'Time Utilisation Error:',
      error,
    );

    return mapTimeUtilisation(
      EMPTY_TIME_UTILISATION,
    );
  }
};

/* ============================================================
 * WTD ACTIVE HOURS
 * ============================================================ */

export const getWtdActiveHours = async (
  request: WtdActiveHoursRequest,
): Promise<WtdActiveHoursResponse[]> => {
  try {
    if (ENV.USE_MOCK) {
      return mapWtdActiveHours([]);
    }

    const { data } =
      await api.post<WtdActiveHoursResponse[]>(
        ENDPOINTS.EMPLOYEE_WTD_ACTIVE_HOURS,
        request,
      );

    return mapWtdActiveHours(data);
  } catch (error) {
    if (axios.isAxiosError(error)) {
      switch (error.response?.status) {
        case 404:
        case 500:
          return [];
      }
    }

    console.error(
      'WTD Active Hours Error:',
      error,
    );

    return [];
  }
};

/* ============================================================
 * ME VS TEAM
 * ============================================================ */

export const getMeVsTeam = async (
  request: MeVsTeamRequest,
): Promise<MeVsTeamResponse> => {
  try {
    if (ENV.USE_MOCK) {
      return mapMeVsTeam(
        EMPTY_ME_VS_TEAM,
      );
    }

    const { data } =
      await api.post<MeVsTeamResponse>(
        ENDPOINTS.EMPLOYEE_ME_VS_TEAM,
        request,
      );

    return mapMeVsTeam(data);
  } catch (error) {
    if (axios.isAxiosError(error)) {
      switch (error.response?.status) {
        case 404:
        case 500:
          return mapMeVsTeam(
            EMPTY_ME_VS_TEAM,
          );
      }
    }

    console.error(
      'Me Vs Team Error:',
      error,
    );

    return mapMeVsTeam(
      EMPTY_ME_VS_TEAM,
    );
  }
};

/* ============================================================
 * FOCUS BLOCKS
 * ============================================================ */

export const getFocusBlocks = async (
  request: FocusBlocksRequest,
): Promise<FocusBlocksResponse> => {
  try {
    if (ENV.USE_MOCK) {
      return mapFocusBlocks(
        EMPTY_FOCUS_BLOCKS,
      );
    }

    const { data } =
      await api.post<FocusBlocksResponse>(
        ENDPOINTS.EMPLOYEE_FOCUS_BLOCKS,
        request,
      );

    return mapFocusBlocks(data);
  } catch (error) {
    if (axios.isAxiosError(error)) {
      switch (error.response?.status) {
        case 404:
        case 500:
          return mapFocusBlocks(
            EMPTY_FOCUS_BLOCKS,
          );
      }
    }

    console.error(
      'Focus Blocks Error:',
      error,
    );

    return mapFocusBlocks(
      EMPTY_FOCUS_BLOCKS,
    );
  }
};

/* ============================================================
 * HISTORY 30 DAYS
 * ============================================================ */

export const getHistory30Days = async (
  request: ActiveHoursHistory30DaysRequest,
): Promise<ActiveHoursHistory30DaysResponse> => {
  try {
    if (ENV.USE_MOCK) {
      return mapHistory30Days(
        EMPTY_HISTORY,
      );
    }

    const { data } =
      await api.post<ActiveHoursHistory30DaysResponse>(
        ENDPOINTS.EMPLOYEE_ACTIVE_HOURS_HISTORY_30_DAYS,
        request,
      );

    return mapHistory30Days(data);
  } catch (error) {
    if (axios.isAxiosError(error)) {
      switch (error.response?.status) {
        case 404:
        case 500:
          return mapHistory30Days(
            EMPTY_HISTORY,
          );
      }
    }

    console.error(
      'History 30 Days Error:',
      error,
    );

    return mapHistory30Days(
      EMPTY_HISTORY,
    );
  }
};

/* ============================================================
 * WEEKLY ACTIVE HOURS DETAIL
 * ============================================================ */

export const getWeeklyActiveHoursDetail = async (
  request: WeeklyActiveHoursDetailRequest,
): Promise<WeeklyActiveHoursDetailResponse[]> => {
  try {
    if (ENV.USE_MOCK) {
      return mapWeeklyActiveHoursDetail([]);
    }

    const { data } =
      await api.post<WeeklyActiveHoursDetailResponse[]>(
        ENDPOINTS.EMPLOYEE_WEEKLY_ACTIVE_HOURS_DETAIL,
        request,
      );

    return mapWeeklyActiveHoursDetail(data);
  } catch (error) {
    if (axios.isAxiosError(error)) {
      switch (error.response?.status) {
        case 404:
        case 500:
          return [];
      }
    }

    console.error(
      'Weekly Active Hours Detail Error:',
      error,
    );

    return [];
  }
};